import { inputList } from '../derive/input-list.js';
import { monitorList } from '../derive/monitor-list.js';
import { networkList } from '../derive/network-list.js';
import { patchList } from '../derive/patch-list.js';
import { powerPlan, powerSummary } from '../derive/power-plan.js';
import { deepEqual } from '../diff/deep-equal.js';
import { diffBoards } from '../diff/diff.js';
import type { BoardData, CollectionName } from '../model/schema.js';
import { COLLECTIONS } from '../model/schema.js';
import { standList } from '../stands/stands.js';
import { topicOf } from './topics.js';
import type {
  CatchupTable,
  ChangeKind,
  ChangeSet,
  EntityPosition,
  PlotChange,
  RowChange,
} from './types.js';
import { CATCHUP_TABLES } from './types.js';

type Entity = Record<string, unknown>;

function firstLine(text: unknown): string {
  return typeof text === 'string' ? text.split('\n', 1)[0]!.trim() : '';
}

/** Human-readable name of an entity for the change list (may be empty). */
export function entityLabel(collection: CollectionName, entity: unknown): string {
  const e = entity as Entity;
  switch (collection) {
    case 'items':
      return (e.label as string) || (e.kind as string);
    case 'channels':
    case 'mixes':
      return `${e.number as number} ${e.name as string}`;
    case 'power':
    case 'frames':
    case 'layers':
      return e.name as string;
    case 'freeform':
      return firstLine(e.text) || (e.kind as string);
    case 'connections':
      return (e.label as string | undefined) ?? '';
    default:
      return '';
  }
}

/** Position of a canvas object (items, free-form elements); undefined for other collections. */
export function entityPosition(
  collection: CollectionName,
  entity: unknown,
): EntityPosition | undefined {
  const e = entity as Entity;
  if (collection !== 'items' && collection !== 'freeform') return undefined;
  const pos: EntityPosition = {
    x: e.x as number,
    y: e.y as number,
    rotation: e.rotation as number,
  };
  const w = collection === 'items' ? e.width : e.w;
  const h = collection === 'items' ? e.depth : e.h;
  if (w !== undefined) pos.w = w as number;
  if (h !== undefined) pos.h = h as number;
  return pos;
}

function plotChange(
  collection: CollectionName,
  id: string,
  kind: ChangeKind,
  fields: string[],
  oldData: BoardData,
  newData: BoardData,
): PlotChange {
  const before = (oldData[collection] as Record<string, Entity>)[id];
  const after = (newData[collection] as Record<string, Entity>)[id];
  const entity = (after ?? before)!;
  const change: PlotChange = {
    type: 'plot',
    key: `${collection}:${id}`,
    collection,
    id,
    kind,
    fields,
    topic: topicOf(after ? newData : oldData, collection, entity),
    label: entityLabel(collection, entity),
  };
  const b = before && entityPosition(collection, before);
  const a = after && entityPosition(collection, after);
  if (b) change.before = b;
  if (a) change.after = a;
  return change;
}

interface Row {
  rowKey: string;
}

/** Row-level diff of one derived table, keyed by `rowKey`; rows keep the new table's order. */
export function diffRows(
  table: CatchupTable,
  oldRows: readonly Row[],
  newRows: readonly Row[],
): RowChange[] {
  const before = new Map(oldRows.map((r) => [r.rowKey, r as unknown as Entity]));
  const after = new Set(newRows.map((r) => r.rowKey));
  const out: RowChange[] = [];
  for (const row of newRows) {
    const old = before.get(row.rowKey);
    if (old === undefined) {
      out.push({ table, rowKey: row.rowKey, kind: 'added', fields: [] });
      continue;
    }
    const cur = row as unknown as Entity;
    const fields = [...new Set([...Object.keys(old), ...Object.keys(cur)])]
      .filter((k) => !deepEqual(old[k], cur[k]))
      .sort();
    if (fields.length > 0) out.push({ table, rowKey: row.rowKey, kind: 'changed', fields });
  }
  for (const row of oldRows)
    if (!after.has(row.rowKey))
      out.push({ table, rowKey: row.rowKey, kind: 'removed', fields: [] });
  return out;
}

const TABLES: Readonly<Record<CatchupTable, (d: BoardData) => readonly Row[]>> = {
  input: inputList,
  monitors: monitorList,
  patch: (d) => patchList(d),
  'power-loads': powerPlan,
  'power-sources': powerSummary,
  network: networkList,
  stands: (d) => standList(d),
};

/** Changed rows of every derived table (input, monitors, patch, power, network, stands). */
export function rowChanges(oldData: BoardData, newData: BoardData): RowChange[] {
  return CATCHUP_TABLES.flatMap((t) => diffRows(t, TABLES[t](oldData), TABLES[t](newData)));
}

/**
 * Everything that differs between two board states (decision C1: `diffBoards(state of visit N,
 * current)`): entity changes with topic, label and positions, plus impacted table rows.
 */
export function changeSet(oldData: BoardData, newData: BoardData): ChangeSet {
  const diff = diffBoards(oldData, newData);
  const plot: PlotChange[] = [];
  if (diff.meta.length > 0)
    plot.push({
      type: 'plot',
      key: 'meta',
      collection: 'meta',
      id: 'meta',
      kind: 'changed',
      fields: diff.meta,
      topic: 'general',
      label: newData.meta.name,
    });
  for (const name of COLLECTIONS) {
    const c = diff.collections[name];
    const entries = new Map<string, [ChangeKind, string[]]>();
    for (const id of c.added) entries.set(id, ['added', []]);
    for (const id of c.removed) entries.set(id, ['removed', []]);
    for (const ch of c.changed) entries.set(ch.id, ['changed', ch.fields]);
    for (const id of [...entries.keys()].sort()) {
      const [kind, fields] = entries.get(id)!;
      plot.push(plotChange(name, id, kind, fields, oldData, newData));
    }
  }
  const rows = diff.empty ? [] : rowChanges(oldData, newData);
  // Derived tables are functions of the board, so rows only change together with entities.
  return { plot, rows, empty: diff.empty };
}
